import React, { useState } from 'react';
import { Box, FormControl, Select, MenuItem, Typography, useTheme, Grid, Paper, IconButton, Chip } from '@mui/material';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import dayjs from 'dayjs';

interface ToolSelectorProps {
  location: string;
  setLocation: (location: string) => void;
  toolType: string;
  setToolType: (toolType: string) => void;
  selectedTool: string;
  setSelectedTool: (tool: string) => void;
  factoryLocations: string[];
  toolTypes: string[];
  etchingTools: string[];
}

export default function ToolSelector({
  location,
  setLocation,
  toolType,
  setToolType,
  selectedTool,
  setSelectedTool,
  factoryLocations,
  toolTypes,
  etchingTools
}: ToolSelectorProps) {
  const theme = useTheme();

  // Get available tools based on selected tool type
  const getAvailableTools = () => {
    switch (toolType) {
      case 'Etching':
        return etchingTools;
      case 'Deposition':
        return ['PECVD 1', 'PECVD 2', 'LPCVD 1', 'LPCVD 2', 'ALD 1', 'ALD 2'];
      default:
        return etchingTools;
    }
  };

  const availableTools = getAvailableTools();

  // Update selected tool when tool type changes
  React.useEffect(() => {
    const tools = getAvailableTools();
    if (!tools.includes(selectedTool)) {
      setSelectedTool(tools[0]);
    }
  }, [toolType, selectedTool, setSelectedTool]);

  const getColorForChannel = (ch: string, idx: number) => {
    const palette = [
      '#00adef', '#8dc63f', '#ff9800', '#e91e63', '#9c27b0', '#3f51b5', '#009688', '#cddc39', '#ff5722', '#607d8b',
      '#795548', '#f44336', '#2196f3', '#4caf50', '#ffc107', '#673ab7', '#ffeb3b', '#b71c1c', '#1b5e20', '#0d47a1',
    ];
    if (idx < palette.length) return palette[idx];
    const hue = (idx * 47) % 360;
    return `hsl(${hue}, 70%, 50%)`;
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      {/* Factory Location Selector */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
          Location:
        </Typography>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <Select
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            sx={{
              border: '1.5px solid #00adef',
              bgcolor: '#fff',
              borderRadius: 24,
              minWidth: 160,
              height: 44,
              px: 2,
              py: 1,
              fontWeight: 700,
              fontSize: '1.1rem',
            }}
            MenuProps={{ PaperProps: { sx: { borderRadius: 2 } } }}
          >
            {factoryLocations.map((loc) => (
              <MenuItem key={loc} value={loc}>
                {loc}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Tool Type Selector */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
          Tool Type:
        </Typography>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <Select
            value={toolType}
            onChange={(e) => setToolType(e.target.value)}
            sx={{
              border: '1.5px solid #00adef',
              bgcolor: '#fff',
              borderRadius: 24,
              minWidth: 160,
              height: 44,
              px: 2,
              py: 1,
              fontWeight: 700,
              fontSize: '1.1rem',
            }}
            MenuProps={{ PaperProps: { sx: { borderRadius: 2 } } }}
          >
            {toolTypes.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Specific Tool Selector */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
          Tool:
        </Typography>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <Select
            value={selectedTool}
            onChange={(e) => setSelectedTool(e.target.value)}
            sx={{
              border: '1.5px solid #00adef',
              bgcolor: '#fff',
              borderRadius: 24,
              minWidth: 160,
              height: 44,
              px: 2,
              py: 1,
              fontWeight: 700,
              fontSize: '1.1rem',
            }}
            MenuProps={{ PaperProps: { sx: { borderRadius: 2 } } }}
          >
            {availableTools.map((tool) => (
              <MenuItem key={tool} value={tool}>
                {tool}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
} 